import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Subject } from 'rxjs';
import { ExecutionEnvironment } from '@telespot/domain';
import { LocalizedName } from '@protocols/domain';

/**
 * The compatibility result between an algorithm and a pipeline step.
 *
 * Modelled as a discriminated (tagged) union — the idiomatic TypeScript
 * analogue of a sealed-class hierarchy — so that individual variants can carry
 * the data that explains them. In particular, `label_mismatch` carries the
 * exact labels the pipeline step requires but the algorithm does not output.
 */
export type CompatibilityState =
  /**
   * The algorithm is compatible with the pipeline.
   */
  | { kind: 'compatible' }

  /**
   * Not compatible: an OCR task requires MLKit.
   */
  | { kind: 'no_ocr' }

  /**
   * Not compatible: a mapping task requires a map engine.
   */
  | { kind: 'no_mapping' }

  /**
   * The pipeline step requires labels that are not present in the algorithm's
   * output. `mismatchedLabels` holds those offending labels — uuid plus its
   * localized name — so the UI can show the user exactly what is causing the
   * mismatch. When a name could not be resolved, `name` falls back to
   * `{ en: uuid }`.
   */
  | { kind: 'label_mismatch'; mismatchedLabels: Array<{ uuid: string; name: LocalizedName }> };

/**
 * The context the smart switcher needs to operate. The consumer (dialog
 * wrapper, story host, ...) seeds this onto the state before the smart
 * component subscribes; the facade reacts to it.
 */
export interface SwitchAlgorithmData {
  currentAlgorithmId: string;
  algorithmExecutionEnvironment: ExecutionEnvironment;
  pipelineId: string;
  pipelineName: string | { [key: string]: string };
  pipelineStepId: string;
  pipelineUsage: Array<{
    id: string;
    name: string | { [key: string]: string };
    workspaces: Record<string, { id: string; name: string | { [key: string]: string } }>;
  }>;
}

export interface AlgorithmSwitcherStateModel {
  /**
   * The data context the switcher operates on. Seeded by the consumer.
   */
  data: SwitchAlgorithmData | undefined;

  /**
   * The id of the algorithm to switch to.
   */
  algorithmToSwitchToId: string | undefined;

  /**
   * The initial search term.
   * This is used to restore the search term when the dialog is reopened.
   */
  initialSearchTerm: string | undefined;

  /**
   * When the user is trying to switch to a deprecated algorithm.
   */
  usingDeprecated: boolean;

  /**
   * The compatibility state of the currently selected algorithm with the pipeline.
   */
  compatibilityState: CompatibilityState | undefined;

  /**
   * The i18n key of the hint shown (and announced) on the algorithm selector.
   * Host presentation context, seeded like `data`: the initial value is the
   * standalone switch-algorithm dialog's own hint, so only hosts that need
   * different wording — e.g. the pipeline step editor — seed their key.
   */
  hintKey: string;
}

const INITIAL_STATE: AlgorithmSwitcherStateModel = {
  data: undefined,
  algorithmToSwitchToId: undefined,
  initialSearchTerm: undefined,
  usingDeprecated: false,
  compatibilityState: undefined,
  hintKey: 'table.algorithms.switch_algorithm_dialog.hint',
};

@Injectable()
export class AlgorithmSwitcherState extends ComponentStore<AlgorithmSwitcherStateModel> {
  public readonly cancellation$ = new Subject<void>();
  public readonly acceptance$ = new Subject<string>();

  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly data$ = this.select((state) => state.data);
  public readonly algorithmToSwitchToId$ = this.select((state) => state.algorithmToSwitchToId);
  public readonly initialSearchTerm$ = this.select((state) => state.initialSearchTerm);
  public readonly usingDeprecated$ = this.select((state) => state.usingDeprecated);
  public readonly compatibilityState$ = this.select((state) => state.compatibilityState);
  public readonly hintKey$ = this.select((state) => state.hintKey);

  // ============================================================================
  // Updaters
  // ============================================================================

  public readonly setData = this.updater((state, data: SwitchAlgorithmData) => ({ ...state, data }));

  public readonly setAlgorithmToSwitchToId = this.updater((state, algorithmToSwitchToId: string | undefined) => ({
    ...state,
    algorithmToSwitchToId,
  }));

  public readonly setInitialSearchTerm = this.updater((state, initialSearchTerm: string | undefined) => ({
    ...state,
    initialSearchTerm,
  }));

  public readonly setUsingDeprecated = this.updater((state, usingDeprecated: boolean) => ({
    ...state,
    usingDeprecated,
  }));

  public readonly setCompatibilityState = this.updater((state, compatibilityState: CompatibilityState | undefined) => ({
    ...state,
    compatibilityState,
  }));

  public readonly setHintKey = this.updater((state, hintKey: string) => ({ ...state, hintKey }));

  public readonly reset = this.updater(() => ({ ...INITIAL_STATE }));

  override ngOnDestroy(): void {
    this.cancellation$.complete();
    this.acceptance$.complete();
    super.ngOnDestroy();
  }
}
